import { FunctionComponent, useEffect, useState } from "react";

import TourHelpPage from "@/components/TourHelpPage";
import FullScreenLoader from "@/components/common/FullScreenLoader";
import { StateType, useAppState } from "@/context/AppState";
import { useParams } from "@/hooks/queryParams";
import { TourType } from "@/types";
import { fetchTourWithoutAuth } from "@/utils/tour";

import RequireAuthentication from "./RequireAuthentication";
import RequireTourAndProspect from "./RequireTourAndProspect";

/**
 * A higher order component that requires tour to be loaded in _some_ form in
 * order to render the WrappedComponent.
 *
 * Prior to the user attaching themselves as a prospect to the tour, they're
 * only able to load the tour via the public (unauthenticated) endpoint, which
 * only returns an abbreviated version of the tour, with much of the data
 * missing. But after a prospect is attached to the tour, they're _required_ to
 * authenticate in order to load the tour _at all_. Because of the complexity
 * inherent in those requirements, this higher order component follows this
 * strategy:
 *
 *   - Attempt to get the tour from the "public" endpoint. (If that works,
 *     render the wrapped Component.)
 *   - If that endpoint returns 401, that means the tour is attached to a
 *     prospect. (And we know the phone number of that prospect.) Therefore,
 *     authenticate the user without gathering their phone number. (Which will
 *     send an SMS with a 6-digit One Time Password to the prospect that owns the
 *     tour.)
 *   - Once they enter the 6-digit code, we have an access token, so now
 *     fetch the tour using the endpoint that requires authentication.
 */
export default function RequireTour(
  WrappedComponent: FunctionComponent<{ tour: TourType }>
) {
  return function RequireTourComponent() {
    const [fetchWithoutAuthFailed, setFetchWithoutAuthFailed] = useState(false);
    const { tourToken } = useParams();
    const [appState, setAppState] = useAppState();

    useEffect(() => {
      const fetchTour = async () => {
        if (appState.tour) {
          // I guess we already have the tour; stop here since we've
          // already met the requirements and are rendering the wrapped component
          return;
        }

        // Initially fetch tour without auth
        const tour = await fetchTourWithoutAuth(tourToken);

        if (tour) {
          setAppState((appState: StateType) => ({ ...appState, tour }));
        } else {
          setFetchWithoutAuthFailed(true);
        }
      };

      fetchTour();
    }, [appState.tour, setAppState, tourToken]);

    if (appState.tour) {
      if (
        appState.tour.deletedAt ||
        appState.tour.isFinalized ||
        appState.tour.identityVerificationResult === "rejected"
      ) {
        return <TourHelpPage tour={appState.tour} />;
      }
      return <WrappedComponent tour={appState.tour} />;
    } else {
      if (fetchWithoutAuthFailed) {
        // The tour couldn't be fetched without auth, which means
        // it's attached to a prospect and requires authentication.
        //
        // Authenticate the user, and then fetch the tour. Upon success,
        // appState.tour will be updated, and we'll render the wrapped
        // component up above.
        //
        // We use and abuse other higher order components here to
        // avoid needless code duplication.
        const WrappedComponent = RequireAuthentication(
          RequireTourAndProspect(() => null)
        );

        return <WrappedComponent />;
      } else {
        // We don't have the tour, but fetching without auth
        // hasn't failed yet, which means we're in the middle
        // of making the unauthenticated request for the tour.
        return <FullScreenLoader />;
      }
    }
  };
}
